import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { canWrite, canChangeStatus, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';
import { computeShippingCost, getShippingConfig } from '@/services/shipping';

export interface OrderItem {
  id: string;
  productName: string;
  sizeLabel: string;
  quantity: number;
  unitPrice: number;
}

export interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  customerCity: string;
  customerPhone: string;
  customerEmail: string;
  notes: string;
  status: string;
  channel: string;
  production: boolean;
  total: number;
  createdAt: string;
  items: OrderItem[];
  paymentMethod: string;
  paymentStatus: string;
  saleId: string;
  saleNumber: string;
  siblingOrderNumber: string;
  // Datos de despacho (Interrapidísimo). Los pedidos creados desde la tienda
  // guardan el destinatario y la dirección; las ventas directas sin envío no.
  hasShipping: boolean;
  requiresShipping: boolean;
  shippingCost: number;
  // Costo estimado de Interrapidísimo para líneas premium (flete incluido en el
  // precio). Es solo de control interno de gastos; no se cobra al cliente.
  estimatedShippingCost: number;
  // Recargo por pago contraentrega cobrado al cliente (solo babuchas sencillas).
  codSurcharge: number;
  // Comisión de Interrapidísimo estimada sobre el pedido recolectado
  // contraentrega. Control interno de gastos; no se cobra a las líneas premium.
  estimatedCodCommission: number;
  recipientName: string;
  recipientPhone: string;
  recipientIdNumber: string;
  recipientEmail: string;
  shippingCity: string;
  shippingDepartment: string;
  shippingAddress: string;
  // Datos del financiamiento Sistecrédito (se llenan cuando el enrolamiento
  // devuelve el número de cuotas y el ID de crédito en la aprobación).
  sistecreditoInstallments: number | null;
  sistecreditoCreditId: string;
  // El financiamiento Sistecrédito fue rechazado/cancelado por el aliado.
  financingRejected: boolean;
  // Estado del último intento de Sistecrédito (approved/pending/rejected/cancelled)
  // y el motivo del rechazo devuelto por el aliado, para el resumen del admin.
  sistecreditoAttemptStatus: string;
  sistecreditoRejectionReason: string;
  // Historial COMPLETO de intentos de Sistecrédito (del más reciente al más
  // antiguo), para mostrar cada intento y no solo el último.
  sistecreditoAttempts: SistecreditoAttempt[];
}

// Un intento individual de financiamiento con Sistecrédito (cada fila de pago
// `sistecredito` del pedido). Sirve para reconstruir el HISTORIAL completo de
// intentos, no solo el más reciente.
export interface SistecreditoAttempt {
  id: string;
  status: string;
  installments: number | null;
  creditId: string;
  rejectionReason: string;
  transactionRef: string;
  amount: number;
  createdAt: string;
}

export interface OrdersResult {
  data: Order[];
}

export interface ConfirmSaleResult {
  success: boolean;
  saleId?: string;
  saleNumber?: string;
  error?: string;
}

// Consulta los pedidos reales (con sus items, productos, tallas y clientes
// desnormalizados) con fallback automático al mock.
export async function fetchOrders(): Promise<OrdersResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [ordersRes, itemsRes, variantsRes, productsRes, sizesRes, customersRes, paymentsRes, salesRes] =
      await Promise.all([
        supabase.from('orders').select('*'),
        supabase.from('order_items').select('*'),
        supabase.from('product_variants').select('*'),
        supabase.from('products').select('id, name'),
        supabase.from('sizes').select('*'),
        supabase.from('customers').select('id, first_name, last_name, city, phone, email'),
        supabase.from('payments').select('id, order_id, method, status, amount, transaction_ref, installments, credit_id, rejection_reason, created_at'),
        supabase.from('sales').select('id, sale_number'),
      ]);

    if (ordersRes.error) throw new Error(ordersRes.error.message);
    if (itemsRes.error) throw new Error(itemsRes.error.message);
    if (variantsRes.error) throw new Error(variantsRes.error.message);
    if (productsRes.error) throw new Error(productsRes.error.message);
    if (sizesRes.error) throw new Error(sizesRes.error.message);
    if (customersRes.error) throw new Error(customersRes.error.message);

    const orders = (ordersRes.data ?? []) as Array<Record<string, any>>;

    const variantMap = new Map(
      ((variantsRes.data ?? []) as Array<Record<string, any>>).map((v) => [v.id, v]),
    );
    const productMap = new Map(
      ((productsRes.data ?? []) as Array<Record<string, any>>).map((p) => [p.id, p.name]),
    );
    const sizeMap = new Map(
      ((sizesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s]),
    );
    const customerMap = new Map(
      ((customersRes.data ?? []) as Array<Record<string, any>>).map((c) => [c.id, c]),
    );

    // Mapa de venta -> número, para vincular pedidos de producción con su venta
    // directa de origen (sale_id).
    const saleNumberMap = new Map(
      ((salesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s.sale_number]),
    );

    // Mapa de grupo -> números de pedido, para vincular pedidos hermanos en una
    // compra combinada (normal + producción) que comparten group_id.
    const groupMap = new Map<string, string[]>();
    orders.forEach((o) => {
      if (o.group_id) {
        const list = groupMap.get(o.group_id) ?? [];
        list.push(o.order_number);
        groupMap.set(o.group_id, list);
      }
    });

    const paymentsByOrder = new Map<string, { method: string; approved: number; total: number; installments: number | null; installmentsAt: string; creditId: string; creditAt: string; financingRejected: boolean; attemptStatus: string; rejectionReason: string; rejectionAt: string; attemptAt: string; attempts: SistecreditoAttempt[] }>();
    ((paymentsRes.data ?? []) as Array<Record<string, any>>).forEach((p) => {
      const entry = paymentsByOrder.get(p.order_id) ?? {
        method: '',
        approved: 0,
        total: 0,
        installments: null,
        installmentsAt: '',
        creditId: '',
        creditAt: '',
        financingRejected: false,
        attemptStatus: '',
        rejectionReason: '',
        rejectionAt: '',
        attemptAt: '',
        attempts: [],
      };
      if (p.method) entry.method = p.method;
      entry.total += Number(p.amount ?? 0);
      if (p.status === 'approved') entry.approved += Number(p.amount ?? 0);
      // Resumen del financiamiento Sistecrédito. El ESTADO se toma del intento
      // más reciente; las cuotas / ID de crédito / motivo se toman del intento
      // más reciente que TENGA ese dato, para no perderlos si después se
      // reintenta con otro método (que añade una fila nueva sin esos datos).
      if (p.method === 'sistecredito') {
        // Registra cada intento individual para armar el historial completo.
        entry.attempts.push({
          id: String(p.id ?? ''),
          status: p.status ? String(p.status) : '',
          installments:
            p.installments === undefined || p.installments === null ? null : Number(p.installments),
          creditId: String(p.credit_id ?? ''),
          rejectionReason: String(p.rejection_reason ?? ''),
          transactionRef: String(p.transaction_ref ?? ''),
          amount: Number(p.amount ?? 0),
          createdAt: p.created_at ? String(p.created_at) : '',
        });
        const at = p.created_at ? String(p.created_at) : '';
        const isLatest = !entry.attemptAt || at >= entry.attemptAt;
        if (isLatest) {
          entry.attemptAt = at;
          entry.attemptStatus = p.status ? String(p.status) : '';
        }
        if (p.installments !== undefined && p.installments !== null) {
          const atI = p.created_at ? String(p.created_at) : '';
          if (!entry.installmentsAt || atI >= entry.installmentsAt) {
            entry.installmentsAt = atI;
            entry.installments = Number(p.installments);
          }
        }
        if (p.credit_id) {
          const atC = p.created_at ? String(p.created_at) : '';
          if (!entry.creditAt || atC >= entry.creditAt) {
            entry.creditAt = atC;
            entry.creditId = String(p.credit_id);
          }
        }
        if (p.rejection_reason) {
          const atR = p.created_at ? String(p.created_at) : '';
          if (!entry.rejectionAt || atR >= entry.rejectionAt) {
            entry.rejectionAt = atR;
            entry.rejectionReason = String(p.rejection_reason);
          }
        }
        // Rechazo/cancelación del aliado sobre un pago aún no aprobado.
        if (p.status === 'rejected' || p.status === 'cancelled') {
          entry.financingRejected = true;
        }
      }
      paymentsByOrder.set(p.order_id, entry);
    });

    const itemsByOrder = new Map<string, OrderItem[]>();
    ((itemsRes.data ?? []) as Array<Record<string, any>>).forEach((oi) => {
      const variant = variantMap.get(oi.variant_id);
      const productName = variant ? productMap.get(variant.product_id) ?? '' : '';
      const size = variant ? sizeMap.get(variant.size_id) : undefined;
      const item: OrderItem = {
        id: oi.id,
        productName,
        sizeLabel: size?.name ?? '',
        quantity: oi.quantity,
        unitPrice: Number(oi.unit_price),
      };
      const list = itemsByOrder.get(oi.order_id) ?? [];
      list.push(item);
      itemsByOrder.set(oi.order_id, list);
    });

    const data = orders
      .map((o) => {
        const customer = customerMap.get(o.customer_id);
        const pay = paymentsByOrder.get(o.id);
        const requiresShipping = Boolean(o.requires_shipping);
        const shippingCost = Number(o.shipping_cost ?? 0);
        const estimatedShippingCost = Number(o.estimated_shipping_cost ?? 0);
        const codSurcharge = Number(o.cod_surcharge ?? 0);
        const estimatedCodCommission = Number(o.estimated_cod_commission ?? 0);
        const shippingAddress = o.shipping_address ?? '';
        const shippingCity = o.shipping_city ?? '';
        // Un pedido "requiere despacho" si se marcó así, si tiene dirección de
        // envío o si se le cobró flete.
        const hasShipping =
          requiresShipping || shippingCost > 0 || Boolean(shippingAddress) || Boolean(shippingCity);
        return {
          id: o.id,
          orderNumber: o.order_number,
          customerName: customer
            ? `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim()
            : '',
          customerCity: customer?.city ?? '',
          customerPhone: customer?.phone ?? '',
          customerEmail: customer?.email ?? '',
          notes: o.notes ?? '',
          status: o.status,
          channel: o.channel ?? (o.utm_source ? 'ecommerce' : 'direct'),
          production: o.production ?? false,
          total: Number(o.total),
          createdAt: o.created_at,
          items: itemsByOrder.get(o.id) ?? [],
          paymentMethod: o.payment_method || pay?.method || '',
          paymentStatus: pay && pay.total > 0 && pay.approved >= pay.total ? 'approved' : 'pending',
          saleId: o.sale_id ?? '',
          saleNumber: o.sale_id ? saleNumberMap.get(o.sale_id) ?? '' : '',
          siblingOrderNumber: o.group_id
            ? (groupMap.get(o.group_id) ?? []).filter((n) => n !== o.order_number)[0] ?? ''
            : '',
          hasShipping,
          requiresShipping,
          shippingCost,
          estimatedShippingCost,
          codSurcharge,
          estimatedCodCommission,
          recipientName: o.recipient_name ?? '',
          recipientPhone: o.recipient_phone ?? '',
          recipientIdNumber: o.recipient_id_number ?? '',
          recipientEmail: o.recipient_email ?? customer?.email ?? '',
          shippingCity,
          shippingDepartment: o.shipping_department ?? '',
          shippingAddress,
          sistecreditoInstallments: pay?.installments ?? null,
          sistecreditoCreditId: pay?.creditId ?? '',
          financingRejected: (pay?.financingRejected ?? false) && (pay?.approved ?? 0) === 0,
          sistecreditoAttemptStatus: pay?.attemptStatus ?? '',
          sistecreditoRejectionReason: pay?.rejectionReason ?? '',
          sistecreditoAttempts: (pay?.attempts ?? [])
            .slice()
            .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
        };
      })
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    return { data };
  } catch (err) {
    console.error('[orders] Supabase connection failed:', err);
    throw err;
  }
}

// Invoca el RPC atómico `confirm_order_sale` en Supabase. Convierte un pedido
// pagado en una venta confirmada: crea sales + sale_items, descuenta inventario
// (con snapshot en inventory_movements), registra comisión, pago y cierra el pedido.
export async function confirmOrderAsSale(
  orderId: string,
  paymentMethod: string,
  transactionRef?: string,
): Promise<ConfirmSaleResult> {
  if (!(await canWrite('orders'))) {
    return { success: false, error: WRITE_PERMISSION_DENIED };
  }

  if (!isSupabaseConfigured || !supabase) {
    return { success: false, error: 'Supabase no está configurado' };
  }

  try {
    const { data, error } = await supabase.rpc('confirm_order_sale', {
      p_order_id: orderId,
      p_payment_method: paymentMethod,
      p_transaction_ref: transactionRef ?? null,
    });

    if (error) {
      console.warn('[orders] confirm_order_sale RPC failed:', error);
      return { success: false, error: error.message };
    }

    const result = data as { success: boolean; sale_id?: string; sale_number?: string };
    if (!result?.success) {
      return { success: false, error: 'El RPC no confirmó la venta' };
    }

    recordAudit({
      entityType: 'orders',
      entityId: orderId,
      action: 'UPDATE',
      fieldName: 'status',
      oldValue: 'paid',
      newValue: 'completed',
    });

    return {
      success: true,
      saleId: result.sale_id,
      saleNumber: result.sale_number,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Error inesperado';
    console.warn('[orders] confirm_order_sale exception:', err);
    return { success: false, error: message };
  }
}

// Reintenta un pedido cancelado por rechazo de Sistecrédito con OTRO método de
// pago. El RPC `retry_order_sale` reabre el pedido, vuelve a reservar el stock,
// convierte el intento rechazado en el pago aprobado del nuevo método y firma
// la venta (o lo deja listo para producir si es un pedido de producción).
export async function retryOrderSale(
  orderId: string,
  paymentMethod: string,
  transactionRef?: string,
): Promise<ConfirmSaleResult> {
  if (!(await canWrite('orders'))) {
    return { success: false, error: WRITE_PERMISSION_DENIED };
  }

  if (!isSupabaseConfigured || !supabase) {
    return { success: false, error: 'Supabase no está configurado' };
  }

  try {
    const { data, error } = await supabase.rpc('retry_order_sale', {
      p_order_id: orderId,
      p_payment_method: paymentMethod,
      p_transaction_ref: transactionRef ?? null,
    });

    if (error) {
      console.warn('[orders] retry_order_sale RPC failed:', error);
      return { success: false, error: error.message };
    }

    const result = data as { success: boolean; sale_id?: string; sale_number?: string; production?: boolean };
    if (!result?.success) {
      return { success: false, error: 'El reintento no confirmó la venta' };
    }

    recordAudit({
      entityType: 'orders',
      entityId: orderId,
      action: 'UPDATE',
      fieldName: 'status',
      oldValue: 'cancelled',
      newValue: result.production ? 'para_produccion' : 'completed',
    });

    return {
      success: true,
      saleId: result.sale_id,
      saleNumber: result.sale_number,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Error inesperado';
    console.warn('[orders] retry_order_sale exception:', err);
    return { success: false, error: message };
  }
}

// Item de pedido manual (variante + cantidad + precios).
export interface OrderItemInput {
  variantId: string;
  quantity: number;
  unitPrice: number;
  unitCost: number;
}

// Datos del destinatario para un pedido manual con envío (guía media carta).
export interface OrderShippingInput {
  recipientName: string;
  recipientPhone: string;
  recipientIdNumber: string;
  recipientEmail: string;
  department: string;
  city: string;
  address: string;
  notes: string;
}

// Entrada de pedido manual (sin pasar por el checkout).
export interface OrderInput {
  customerId: string;
  notes: string;
  items: OrderItemInput[];
  status: 'pending_payment' | 'received' | 'payment_confirmed';
  paymentMethod: string;
  shipping?: OrderShippingInput;
  // Valor de envío editado manualmente en el formulario. Si se define, tiene
  // prioridad sobre el cálculo automático por peso/ciudad.
  shippingCostOverride?: number;
}

export interface CreateOrderResult {
  success: boolean;
  orderId?: string;
  orderNumber?: string;
  error?: string;
}

// Registra un pedido manual en Supabase (orders + order_items). El canal se
// marca como 'direct' (creado desde el panel) y el estado inicial lo elige el
// administrador. Si se indica un método de pago, se registra el pago asociado.
export async function createOrder(input: OrderInput): Promise<CreateOrderResult> {
  if (!(await canWrite('orders'))) return { success: false, error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, error: 'Supabase no está configurado.' };
  }

  const subtotal = input.items.reduce((sum, it) => sum + it.quantity * it.unitPrice, 0);
  // Flete de Interrapidísimo: respeta el valor editado manualmente en el
  // formulario; si no, se calcula por ciudad (0 si no hay envío).
  const shippingCost = input.shipping
    ? (typeof input.shippingCostOverride === 'number'
        ? input.shippingCostOverride
        : computeShippingCost(input.shipping.city, await getShippingConfig()))
    : 0;
  const total = subtotal + shippingCost;
  const orderNumber = `PCP-${Date.now().toString().slice(-6)}`;

  try {
    const { data, error } = await supabase
      .from('orders')
      .insert({
        order_number: orderNumber,
        customer_id: input.customerId,
        channel: 'direct',
        status: input.status,
        subtotal,
        discount_amount: 0,
        shipping_cost: shippingCost,
        total,
        notes: input.notes.trim() || null,
        requires_shipping: Boolean(input.shipping),
        recipient_name: input.shipping?.recipientName.trim() || null,
        recipient_phone: input.shipping?.recipientPhone.trim() || null,
        recipient_id_number: input.shipping?.recipientIdNumber.trim() || null,
        recipient_email: input.shipping?.recipientEmail.trim() || null,
        shipping_city: input.shipping?.city.trim() || null,
        shipping_department: input.shipping?.department.trim() || null,
        shipping_address: input.shipping?.address.trim() || null,
        shipping_notes: input.shipping?.notes.trim() || null,
      })
      .select('id');

    if (error) return { success: false, error: error.message };
    const order = data?.[0] as { id: string } | undefined;
    if (!order) return { success: false, error: 'No se pudo crear el pedido.' };

    const items = input.items.map((it) => ({
      order_id: order.id,
      variant_id: it.variantId,
      quantity: it.quantity,
      unit_price: it.unitPrice,
      unit_cost: it.unitCost,
      discount: 0,
      subtotal: it.quantity * it.unitPrice,
    }));
    const { error: itemsError } = await supabase.from('order_items').insert(items);
    if (itemsError) console.warn('[orders] order_items insert failed:', itemsError.message);

    // Registra el pago asociado al pedido manual (si se eligió un método).
    if (input.paymentMethod) {
      const paymentStatus = input.status === 'payment_confirmed' ? 'approved' : 'pending';
      const { error: payError } = await supabase.from('payments').insert({
        payment_number: `P-${Date.now().toString().slice(-6)}`,
        order_id: order.id,
        method: input.paymentMethod,
        status: paymentStatus,
        amount: total,
        transaction_ref: null,
      });
      if (payError) console.warn('[orders] payments insert failed:', payError.message);
    }

    recordAudit({
      entityType: 'orders',
      entityId: order.id,
      action: 'CREATE',
      newValue: orderNumber,
    });

    return { success: true, orderId: order.id, orderNumber };
  } catch (err) {
    console.warn('[orders] createOrder failed:', err);
    return { success: false, error: 'Error de conexión al crear el pedido.' };
  }
}

// Cambia el estado de un pedido manualmente (received / pending_payment /
// payment_confirmed / preparing / ... / cancelled) dejando registro en auditoría.
export async function updateOrderStatus(
  id: string,
  status: string,
): Promise<{ error: string | null }> {
  if (!(await canChangeStatus('orders'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { data: current } = await supabase
    .from('orders')
    .select('status')
    .eq('id', id)
    .maybeSingle();

  const { error } = await supabase.from('orders').update({ status }).eq('id', id);
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'orders',
    entityId: id,
    action: 'UPDATE',
    fieldName: 'status',
    oldValue: (current as Record<string, any> | null)?.status ?? '',
    newValue: status,
  });
  return { error: null };
}

// Cancela un pedido pendiente de pago y libera de inmediato la reserva de stock
// que tenía apartada (RPC atómico `cancel_order_and_release`). Se usa desde el
// desglose de reservas activas del panel de Inventario. Nunca cancela pedidos
// de producción ni pedidos que ya tengan un pago aprobado.
export async function cancelOrderAndRelease(orderId: string): Promise<{ error: string | null }> {
  if (!(await canChangeStatus('orders'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  try {
    const { data, error } = await supabase.rpc('cancel_order_and_release', {
      p_order_id: orderId,
    });
    if (error) return { error: error.message };

    const result = data as { success: boolean; error?: string } | null;
    if (!result?.success) {
      return { error: result?.error || 'No se pudo cancelar el pedido.' };
    }

    recordAudit({
      entityType: 'orders',
      entityId: orderId,
      action: 'UPDATE',
      fieldName: 'status',
      oldValue: 'pending_payment',
      newValue: 'cancelled',
    });
    return { error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Error al cancelar el pedido.';
    return { error: message };
  }
}

export interface ConfirmProductionResult {
  success: boolean;
  saleId?: string;
  saleNumber?: string;
  error?: string;
}

// Convierte un pedido de producción en una venta al marcarlo como "entregado".
// Antes valida que el producto ya tenga stock (ingresado por Compras); si no,
// devuelve un error claro. El RPC registra venta + inventario + comisión + pago
// del saldo pendiente + donación del período.
export async function confirmProductionOrder(
  orderId: string,
  paymentMethod: string,
): Promise<ConfirmProductionResult> {
  if (!(await canChangeStatus('orders'))) {
    return { success: false, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { success: false, error: 'Supabase no está configurado' };
  }

  try {
    const { data, error } = await supabase.rpc('confirm_production_order', {
      p_order_id: orderId,
      p_payment_method: paymentMethod,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    const result = data as { success: boolean; sale_id?: string; sale_number?: string };
    if (!result?.success) {
      return { success: false, error: 'No se pudo entregar el pedido' };
    }

    recordAudit({
      entityType: 'orders',
      entityId: orderId,
      action: 'UPDATE',
      fieldName: 'status',
      oldValue: 'producido',
      newValue: 'entregado',
    });

    return { success: true, saleId: result.sale_id, saleNumber: result.sale_number };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Error inesperado';
    return { success: false, error: message };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PARTE B: Marcar el pago de un pedido (e-commerce) como pagado / aprobado.
// Actualiza todos los pagos 'pending' del pedido y, si el pedido es parte de
// una compra combinada (normal + producción del mismo cliente), también
// sincroniza el pago del pedido hermano.
// ─────────────────────────────────────────────────────────────────────────────
export async function markOrderAsPaid(orderId: string): Promise<{ error: string | null }> {
  if (!(await canWrite('orders'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  try {
    // 1. Datos del pedido (total, método y grupo) para validar antes de marcar.
    const { data: order } = await supabase
      .from('orders')
      .select('customer_id, production, channel, group_id, total, payment_method')
      .eq('id', orderId)
      .maybeSingle();
    if (!order) return { error: 'No se encontró el pedido.' };

    // 2. Pagos pendientes de este pedido.
    const { data: pendingRows, error: pendingErr } = await supabase
      .from('payments')
      .select('id')
      .eq('order_id', orderId)
      .eq('status', 'pending');
    if (pendingErr) return { error: pendingErr.message };

    const hasPending = (pendingRows ?? []).length > 0;

    if (hasPending) {
      // Camino normal (contra entrega / transferencia): aprobar los pendientes.
      const { error: payErr } = await supabase
        .from('payments')
        .update({ status: 'approved' })
        .eq('order_id', orderId)
        .eq('status', 'pending');
      if (payErr) return { error: payErr.message };
    } else {
      // No hay fila de pago por aprobar (p. ej. ePayco: el pago se crea recién
      // al aprobarse la transacción). Para NO reportar un éxito sin efecto real,
      // se crea el registro de pago correspondiente antes de confirmar.
      const { data: approvedRows, error: approvedErr } = await supabase
        .from('payments')
        .select('amount')
        .eq('order_id', orderId)
        .eq('status', 'approved');
      if (approvedErr) return { error: approvedErr.message };

      const paid = ((approvedRows ?? []) as Array<Record<string, any>>).reduce(
        (sum, p) => sum + Number(p.amount ?? 0),
        0,
      );
      const total = Number(order.total ?? 0);
      const remaining = Math.max(0, total - paid);

      if (remaining > 0) {
        const method =
          order.payment_method && String(order.payment_method).trim()
            ? String(order.payment_method).trim()
            : 'other';
        const { error: insErr } = await supabase.from('payments').insert({
          payment_number: `P-${Date.now().toString().slice(-6)}`,
          order_id: orderId,
          method,
          status: 'approved',
          amount: remaining,
          transaction_ref: null,
        });
        if (insErr) return { error: insErr.message };
      }
    }

    // 3. Sincroniza el pago del pedido hermano cuando pertenece a una compra
    // combinada (normal + producción) que comparte el mismo group_id.
    if (order.channel === 'ecommerce' && order.group_id) {
      const { data: siblings } = await supabase
        .from('orders')
        .select('id')
        .eq('group_id', order.group_id)
        .neq('id', orderId);

      if (siblings && siblings.length > 0) {
        for (const sibling of siblings as Array<{ id: string }>) {
          await supabase
            .from('payments')
            .update({ status: 'approved' })
            .eq('order_id', sibling.id)
            .eq('status', 'pending');
        }
      }
    }

    recordAudit({
      entityType: 'payments',
      entityId: orderId,
      action: 'UPDATE',
      fieldName: 'status',
      oldValue: 'pending',
      newValue: 'approved',
    });

    return { error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Error inesperado';
    return { error: message };
  }
}

// Actualiza las notas de un pedido (usado en el módulo de Producción).
export async function updateOrderNotes(
  id: string,
  notes: string,
): Promise<{ error: string | null }> {
  if (!(await canWrite('orders'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { error } = await supabase
    .from('orders')
    .update({ notes: notes.trim() || null })
    .eq('id', id);
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'orders',
    entityId: id,
    action: 'UPDATE',
    fieldName: 'notes',
    newValue: notes,
  });
  return { error: null };
}